<template>
  <div data-test-id="model-table">
    <component
      :is="dialog ? 'q-dialog': 'div'"
      v-model="showDialog"
      :maximized="true"
      transition-show="fade-in"
      transition-hide="fade-out"
      @keyup.escape="close()"
    >
      <q-table
        ref="table"
        :key="reactiveTriggerKey"
        v-model:pagination="pagination"
        v-model:selected="selected"
        :dense="$mobileRegister() || denseTable"
        :rows="filteredData"
        :filter="currentFilter"
        :row-key="rowKey"
        :selection="selection"
        :bordered="bordered"
        :flat="flat"
        :square="square"
        wrap-cells
        :loading="collection.loading || loading"
        :columns="processedColumns"
        :hide-bottom="!!filteredData.length && (!paginate || hidePaginate)"
        :no-data-label="collection.isViewPermitted ? `No ${collection.humanCollectionName}` : `You do not have permission to view ${collection.humanCollectionName}`"
        binary-state-sort
        :grid="gridOn"
        :rows-per-page-options="ROWS_PER_PAGE_OPTIONS"
        @row-click="(event, row) => handleRowClick(event, row)"
        @request="onRequest"
      >
        <template
          v-if="!hideTop"
          #top
        >
          <q-btn
            v-if="dialog"
            icon="close"
            flat
            round
            dense
            @click="close"
          />

          <q-space v-if="dialog" />

          <div class="q-table__title row items-center">
            {{ title || collection.humanCollectionName }}
            <model-level-icon
              v-if="showModelLevel"

              class="q-ml-sm"
              :model="collection"
            />
            <audit-messages-icon
              v-if="auditSummary && auditSummary.messages && auditSummary.messages.length"
              :messages="auditSummary.messages"
            />
            <translate-language-select
              v-if="shouldShowLanguageSelect"
              class="q-ml-xs"
              :model-value="currentLanguage"
              :loading="loading || collection.loading"
              :disable="isSubmodel()"
              @update:model-value="changeTranslationLanguage"
            />
          </div>

          <q-space />

          <div class="row q-gutter-sm items-center">
            <slot name="header-controls" />
            <q-inner-loading :showing="loading || collection.loading" />
            <q-btn-toggle
              v-if="grid"
              v-model="gridOn"
              flat
              dense
              :options="[
                { value: false, slot: 'table' },
                { value: true, slot: 'grid' },
              ]"
            >
              <template #table>
                <q-icon
                  right
                  name="table_rows"
                />
              </template>

              <template #grid>
                <q-icon
                  right
                  name="grid_view"
                />
              </template>
            </q-btn-toggle>
            <q-btn
              v-if="showExport"
              icon="download"
              round
              :disable="collection.hasAncestor(collection.library) ? collection.library.loading : collection.loading"
              @click="exportTable"
            />

            <q-btn
              v-if="archivable && collection.isDeletePermitted"
              icon="fas fa-box-archive"
              round
              :color="(submodelArchived || archived) ? 'primary' : 'responsive-grey-8'"
              :outline="!(submodelArchived || archived)"
              @click="toggleArchived"
            >
              <q-tooltip>View Archive</q-tooltip>
            </q-btn>
            <q-btn
              v-if="!isSubmodel()"
              icon="refresh"
              round
              :disable="collection.hasAncestor(collection.library) ? collection.library.loading : collection.loading"
              @click="reloadData"
            >
              <q-tooltip>Reload Data</q-tooltip>
            </q-btn>
            <q-input
              v-if="shouldShowFilter"
              v-model.trim="requestedFilter"
              borderless
              dense
              placeholder="Search"
              clearable
              :bottom-slots="!!(requestedFilter && requestedFilter !== currentFilter)"
              data-test-id="model-table-search-input"
              @keydown.enter="() => currentFilter = requestedFilter"
            >
              <template #after>
                <q-btn
                  color="grey"
                  @click="() => currentFilter = requestedFilter"
                >
                  <q-icon
                    name="search"
                  />
                </q-btn>
              </template>
              <template #hint>
                <div
                  v-if="requestedFilter && requestedFilter !== currentFilter"
                  class="text-negative"
                >
                  Click or Enter to Search
                </div>
              </template>
            </q-input>
            <slot name="header-buttons" />
            <q-inner-loading :showing="loading || collection.loading" />
            <q-btn
              v-for="(button, index) in headerButtons"
              :key="`header-button-${index}`"
              :dense="dense"
              :label="button.label"
              :disable="button.disable"
              @click="button.handler"
            />
            <q-btn
              v-if="orderable && collection.isManagePermitted"
              :dense="dense"
              :disable="!canSetOrder"
              @click="setOrder()"
            >
              <action-not-permitted-tooltip v-if="!collection.isManagePermitted" />
              <slot
                name="set-order-button-label"
              >
                Set Order
              </slot>
            </q-btn>
            <q-btn
              v-if="showAdd && hasLibrary && libraryCollection && collection.isManagePermitted"
              :dense="dense"
              label="Add From Library"
              :disable="collection.isTranslation || !collection.isManagePermitted"
              @click="showAddFromLibraryDialog = true"
            >
              <action-not-permitted-tooltip v-if="!collection.isManagePermitted" />
              <english-required-tooltip v-else-if="collection.isTranslation" />
            </q-btn>
            <q-btn
              v-if="showAdd && collection.isManagePermitted"
              :dense="dense"
              outline
              :label="`${hasLibrary ? 'Create' : 'Add'} ${$_.isNil(addLabel) ? collection.humanTypeName : addLabel}`"
              :disable="collection.isTranslation || !collection.isManagePermitted"
              @click="add()"
            >
              <action-not-permitted-tooltip v-if="!collection.isManagePermitted" />
              <english-required-tooltip v-else-if="collection.isTranslation" />
            </q-btn>
          </div>
          <slot name="header-messages" />
        </template>

        <template #body-cell="props">
          <q-td
            :props="props"
            :class="cellClass && cellClass(props)"
            :style="{ 'max-width': props.col.maxWidth, 'min-width': props.col.minWidth }"
          >
            <template v-if="props.col.loading">
              <q-spinner size="2em" />
            </template>
            <template v-else-if="!editMode || !props.col.editor || props.col.readonly || props.row.readonly">
              <!-- RENDERERS -->
              <template v-if="props.col.required && !props.value">
                <q-icon
                  name="warning"
                  size="md"
                  color="negative"
                >
                  <q-tooltip>
                    Field is required
                  </q-tooltip>
                </q-icon>
              </template>
              <template v-else-if="props.col.component === 'checkmark'">
                <q-icon
                  v-if="props.value"
                  name="check"
                  size="md"
                  color="positive"
                />
              </template>
              <template v-else-if="props.col.component === 'checkmark-or-negative'">
                <q-icon
                  v-if="props.value"
                  name="check"
                  size="md"
                  color="positive"
                />
                <q-icon
                  v-else
                  name="remove"
                  size="md"
                  color="negative"
                />
              </template>
              <template v-else-if="props.col.component === 'download'">
                <q-btn
                  v-if="props.value.inProgress"
                  label="Export In Progress"
                  icon="hourglass_top"
                  disable
                  size="md"
                />
                <q-btn
                  v-else-if="props.value"
                  :label="props.value ? props.value.name || 'Download' : 'No File Selected'"
                  icon="download"
                  :disable="!props.value.link"
                  size="md"
                  @click="download(props.value.link)"
                />
              </template>
              <template v-else-if="props.col.component === 'checkbox'">
                <q-checkbox
                  disable
                  :model-value="props.value"
                />
              </template>
              <template v-else-if="props.col.component === 'percent'">
                <i18n-n
                  :class="{ 'text-grey-7': !props.value, 'text-red': props.value < 0 }"
                  tag="span"
                  :value="props.value"
                  format="decimal0"
                  scope="global"
                /><span :class="{ 'text-grey-7': !props.value, 'text-red': props.value < 0 }">%</span>
              </template>
              <template v-else-if="props.col.component === 'percent-1'">
                <i18n-n
                  :class="{ 'text-grey-7': !props.value, 'text-red': props.value < 0 }"
                  tag="span"
                  :value="props.value"
                  format="decimal1"
                  scope="global"
                /><span :class="{ 'text-grey-7': !props.value, 'text-red': props.value < 0 }">%</span>
              </template>
              <template v-else-if="props.col.component === 'percent-2'">
                <i18n-n
                  :class="{ 'text-grey-7': !props.value, 'text-red': props.value < 0 }"
                  tag="span"
                  :value="props.value"
                  format="decimal2"
                  scope="global"
                /><span :class="{ 'text-grey-7': !props.value, 'text-red': props.value < 0 }">%</span>
              </template>
              <icon-cell
                v-else-if="props.col.component === 'icon'"
                :value="props.value"
              />
              <template v-else-if="props.col.component === 'icons'">
                <div
                  class="row no-wrap justify-start q-gutter-sm"
                  style="max-width: 200px;"
                >
                  <div
                    v-for="(value, index) in props.value"
                    :key="value + ':' + index"
                    class="col-auto"
                  >
                    <icon-cell
                      :value="value"
                    />
                  </div>
                </div>
              </template>
              <template v-else-if="props.col.component === 'model'">
                <model-chip-cell
                  :props="props"
                  :variables="collection.options.variables"
                  @edit-model="({ value, newTab, subpath }) => $editModel(value, newTab, subpath)"
                  @apply-submodel-filter="({ col, value }) => applySubmodelFilter(col, value)"
                />
              </template>
              <template v-else-if="props.col.component === 'chips'">
                <div
                  class="row"
                  style="max-width: 200px;"
                >
                  <div
                    class="col row"
                  >
                    <q-chip
                      v-for="(value, index) in props.expand ? props.value : props.value.slice(0, props.col.limitValues)"
                      :key="value + ':' + index"
                      class="col-auto"
                      :label="value"
                    />
                  </div>
                  <q-btn
                    v-if="props.expand && props.value.length > props.col.limitValues"
                    class="col-auto"
                    icon="unfold_less"
                    size="sm"
                    @click.stop="props.expand = false"
                  />
                  <q-btn
                    v-else-if="!props.expand && props.col.limitValues < props.value.length"
                    class="col-auto"
                    icon="unfold_more"
                    size="sm"
                    @click.stop="props.expand = true"
                  />
                </div>
              </template>
              <template v-else-if="props.col.component === 'custom-component'">
                <slot
                  name="custom-component"
                  v-bind="props"
                />
              </template>
              <template v-else-if="props.col.component === 'library-chips'">
                <div
                  class="row"
                  style="max-width: 200px;"
                >
                  <q-chip
                    v-for="({ description, inheritedFromLibrary }, index) in props.value"
                    :key="description + ':' + index"
                    class="col-auto"
                    :label="description"
                    :icon="inheritedFromLibrary ? 'menu_book' : undefined"
                  />
                </div>
              </template>
              <chip-cell
                v-else-if="props.col.component === 'chip'"
                :props="props"
                @filter="({ name, value }) => $emit('filter', { name, value })"
              />
              <template v-else-if="props.col.component === 'img'">
                <img
                  v-if="props.value"
                  height="60px"
                  :src="props.value"
                >
              </template>
              <template v-else-if="props.col.component === 'color'">
                <div
                  v-if="props.value"
                  style="border: solid 2px; height: 40px; width: 40px; border-radius: 4px;"
                  :style="'color: ' + props.value + '; border-color: ' + props.value + '; background-color: ' + props.value + '33;'"
                />
              </template>
              <money-cell
                v-else-if="props.col.component === 'money' || props.col.component === 'money-count'"
                :props="props"
              />
              <template v-else-if="props.col.component === 'money-hundredths'">
                <span v-if="!props.value || !$_.isNumber(props.value)">—</span>
                <i18n-n
                  v-else
                  :class="{ 'text-grey-responsive-7': !props.value }"
                  tag="span"
                  :value="props.value"
                  format="currencyHundredths"
                  scope="global"
                />
              </template>
              <template v-else-if="props.col.component === 'audit'">
                <div v-if="props.value">
                  <q-icon
                    v-if="computeAuditLevel(props.value.data) === 'error'"
                    size="md"
                    name="error"
                    color="red"
                  />
                  <q-icon
                    v-else-if="computeAuditLevel(props.value.data) === 'warning'"
                    name="warning"
                    color="orange"
                    size="md"
                  />
                  <q-icon
                    v-else-if="computeAuditLevel(props.value.data) === 'info'"
                    name="info"
                    color="blue"
                    size="md"
                  />
                  <q-tooltip>
                    <div
                      v-for="audit in props.value.data"
                      :key="audit.id"
                      name="Audit Data"
                    >
                      <div
                        v-for="message in audit.messages"
                        :key="audit.id + message"
                        name="Audit Message"
                        :style="`color: ${audit.level === 'info' ? 'light-blue' : audit.level === 'warning' ? 'orange' : 'red' }`"
                      >
                        {{ message }}
                      </div>
                    </div>
                  </q-tooltip>
                </div>
              </template>
              <template v-else-if="props.col.component === 'library-link' && hasLibrary">
                <library-link-cell
                  :props="props"
                  :collection="collection"
                  :library-options="libraryCollection.data"
                  :library-filter="linkFilter"
                />
              </template>
              <template v-else-if="props.col.component === 'relative-time'">
                <q-chip v-if="props.value">
                  {{ props.value.relativeTime && props.value.relativeTime() || props.value }}
                  <q-tooltip v-if="props.value.relativeTime">
                    {{ props.value }}
                  </q-tooltip>
                </q-chip>
              </template>
              <template v-else-if="props.col.component === 'duration'">
                <q-chip v-if="props.value">
                  {{ props.value.relativeTime && props.value.relativeTime({ duration: true }) || props.value }}
                  <q-tooltip v-if="props.value.relativeTime">
                    {{ props.value }}
                  </q-tooltip>
                </q-chip>
              </template>
              <template v-else-if="props.col.component === 'date'">
                <q-chip
                  v-if="props.value"
                  square
                  dense
                >
                  {{ props.value }}
                </q-chip>
              </template>
              <date-time-cell
                v-else-if="props.col.component === 'short-date'"
                :props="props"
                format="shortDate"
              />
              <date-time-cell
                v-else-if="props.col.component === 'short-time'"
                :props="props"
                format="shortTime"
              />
              <template v-else-if="props.col.component === 'full-date'">
                <q-chip
                  v-if="props.value && props.value.format('ddd, MMM Do, YYYY')"
                  square
                  dense
                  :size="$q.screen.xs ? 'sm' : 'md'"
                >
                  {{ props.value.format('ddd, MMM Do, YYYY') }}
                </q-chip>
              </template>
              <date-time-cell
                v-else-if="props.col.component === 'date-time'"
                :props="props"
              />
              <template v-else-if="props.col.component === 'movie-rating'">
                <q-btn
                  v-if="props.value"
                  color="yellow-7"
                  outline
                  size="sm"
                  icon="star"
                  round
                >
                  {{ props.value }}
                  <q-tooltip>
                    Rated {{ props.value }} out of 5 Stars
                  </q-tooltip>
                </q-btn>
              </template>
              <template v-else-if="props.col.component === 'link'">
                <q-btn
                  outline
                  :disable="$_.isNil(props.value.link)"
                  :icon-right="props.value.link ? 'open_in_new' : undefined"
                  @click.prevent.stop
                  @click="openLinkInNewTab(props.value.link)"
                >
                  {{ props.value.text }}
                </q-btn>
              </template>
              <template v-else-if="props.col.component === 'visit-rating'">
                <q-icon
                  v-if="props.value && props.value.rating"
                  :color="props.value.color"
                  :name="RATING_ICONS[props.value.rating - 1]"
                  size="md"
                >
                  <q-tooltip>
                    Rated {{ props.value.rating }} out of 4
                  </q-tooltip>
                </q-icon>
              </template>
              <template v-else-if="props.col.component === 'visit-rating-average'">
                <q-icon
                  v-if="props.value.value"
                  :color="RATING_ICONS_COLORS[Math.round(props.value.value) - 1]"
                  :name="RATING_ICONS[Math.round(props.value.value) - 1]"
                  size="md"
                >
                  <q-tooltip>
                    Average Rating {{ Math.round(props.value.value * 100) / 100 }} out of 4
                  </q-tooltip>
                </q-icon>
              </template>
              <template v-else-if="props.col.component === 'effective-type'">
                <q-chip
                  v-if="props.value"
                  square
                  class="q-ml-none"
                  :label="$_.startCase(props.value)"
                  :style="{ 'background-color': props.value.color }"
                >
                  <q-tooltip>
                    <effective-type-detail
                      style="max-width: 600px;"
                      :effective-type="props.value"
                      :show-void-control="false"
                    />
                  </q-tooltip>
                </q-chip>
              </template>

              <template v-else-if="props.col.component === 'html'">
                <!-- eslint-disable vue/no-v-html -->
                <!-- v-html produced by backend / developer should sanitise -->
                <div
                  v-if="props.value"
                  v-html="props.value"
                />
                <!-- eslint-enable vue/no-v-html -->
              </template>
              <template v-else-if="props.col.component === 'amount'">
                <div
                  class="text-column text-bold"
                  :class="props.value > 0 ? 'text-positive' : 'text-negative'"
                >
                  {{ props.value }}
                </div>
              </template>
              <template v-else-if="props.col.component === 'id'">
                <div
                  class="text-column"
                >
                  {{ props.value < 0 ? '—' : props.value }}
                </div>
              </template>
              <template v-else-if="props.col.component">
                <component
                  :is="props.col.component"
                  v-bind="{ props: props }"
                />
              </template>
              <div
                v-else
                class="text-column"
              >
                {{ props.value }}
              </div>
            </template>
            <template v-else>
              <!-- EDITORS -->
              <template v-if="props.col.loading">
                <q-spinner />
              </template>
              <template
                v-else-if="props.col.editor.component === 'select'"
              >
                <q-select
                  v-model="props.row[props.col.name]"
                  class="no-select"
                  :emit-value="props.col.editor.emitValue"
                  :hide-bottom-space="true"
                  :label="props.col.editor.label"
                  :map-options="props.col.editor.mapOptions"
                  :option-label="props.col.editor.optionLabel"
                  :option-value="props.col.editor.optionValue"
                  :options="typeof props.col.editor.options === 'function' ? props.col.editor.options(props.row) : props.col.editor.options"
                  :readonly="!!props.row.committedAt"
                  :rules="[...(props.col.editor.rules || [])]"
                  :clearable="props.col.editor.clearable || false"
                  bottom-slots
                  dense
                  :use-input="!!props.col.editor.filterFunction"
                  :input-debounce="props.col.editor.filterFunction ? 100 : 0"
                  @update:model-value="val => (reactiveTriggerKey += 1) && props.col.editor.inputCallback && props.col.editor.inputCallback(val, props.row)"
                  @filter="(val, update) => props.col.editor.filterFunction ? props.col.editor.filterFunction(val, update) : update(val)"
                />
              </template>
              <template v-else-if="props.col.editor.component === 'input' && props.col.editor.type === 'number'">
                <q-input
                  v-model.number="props.row[props.col.name]"
                  :hide-bottom-space="true"
                  :label="props.col.editor.label"
                  :rules="[...(props.col.editor.rules || [])]"
                  :type="props.col.editor.type"
                  :step="props.col.editor.step"
                  :min="props.col.editor.min"
                  :prefix="props.col.editor.prefix"
                  :disable="props.col.editor.disableFunction ? props.col.editor.disableFunction(props.row) : props.col.editor.disable"
                  bottom-slots
                  dense
                />
              </template>
              <template v-else-if="props.col.editor.component === 'input-with-setter' && props.col.editor.type === 'number'">
                <q-input
                  :model-value="props.value"
                  style="min-width: 100px;"
                  :hide-bottom-space="true"
                  :label="props.col.editor.label"
                  :rules="[...(props.col.editor.rules || [])]"
                  :type="props.col.editor.type"
                  :step="props.col.editor.step"
                  :placeholder="getEditorPlaceholder(props.col, props.row)"
                  :min="props.col.editor.min"
                  :prefix="props.col.editor.prefix"
                  :disable="props.col.editor.disableFunction ? props.col.editor.disableFunction(props.row) : props.col.editor.disable"
                  bottom-slots
                  dense
                  @update:model-value="(val) => props.col.editor.setter(props.row, val)"
                />
              </template>
              <template v-else-if="props.col.editor.component === 'input'">
                <q-input
                  v-model="props.row[props.col.name]"
                  :hide-bottom-space="true"
                  :label="props.col.editor.label"
                  :rules="[...(props.col.editor.rules || [])]"
                  :type="props.col.editor.type"
                  :step="props.col.editor.step"
                  :prefix="props.col.editor.prefix"
                  :disable="props.col.editor.disableFunction ? props.col.editor.disableFunction(props.row) : props.col.editor.disable"
                  bottom-slots
                  dense
                />
              </template>
              <template v-else-if="props.col.editor.component === 'checkbox'">
                <q-checkbox
                  v-model="props.row[props.col.name]"
                  :label="props.col.editor.label"
                  :true-value="props.col.editor.trueValue"
                  :false-value="props.col.editor.falseValue"
                />
              </template>
              <template v-else-if="props.col.editor.component === 'checkbox-action'">
                <q-checkbox
                  :model-value="props.row[props.col.name]"
                  :label="props.col.editor.label"
                  :true-value="props.col.editor.trueValue"
                  :false-value="props.col.editor.falseValue"
                  :disable="props.row[props.col.name] === props.col.editor.trueValue"
                  @update:model-value="val => props.col.editor.inputCallback && props.col.editor.inputCallback(val, props.row)"
                />
              </template>
              <template v-else-if="props.col.editor.component">
                <component
                  :is="props.col.editor.component"
                  v-bind="{ props: props }"
                />
              </template>
              <template v-else>
                {{ props.value }}
              </template>
            </template>
          </q-td>
        </template>

        <template
          #body-cell-actions="props"
        >
          <q-td
            :props="props"
            :class="cellClass && cellClass(props)"
          >
            <slot
              name="row-actions"
              v-bind="props"
            >
              <div class="row q-gutter-sm justify-end no-wrap">
                <slot
                  name="prepend-row-actions"
                  v-bind="props"
                />
                <q-btn
                  v-if="showEdit"
                  :hover="false"
                  :label="editLabel"
                  @click="edit(props.row)"
                />
                <q-btn
                  v-if="showActions(props.row)"
                  :hover="false"
                  icon="more_vert"
                  round
                  @click.stop
                >
                  <q-menu>
                    <q-list style="min-width: 180px">
                      <q-item
                        v-if="showEdit"
                        v-close-popup
                        clickable
                        @click="edit(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="edit" />
                        </q-item-section>
                        <q-item-section>
                          {{ editLabel }}
                        </q-item-section>
                      </q-item>
                      <q-separator v-if="duplicatable && collection.isManagePermitted" />
                      <q-item
                        v-if="duplicatable && collection.isManagePermitted"
                        v-close-popup
                        clickable
                        :disable="collection.isTranslation || !collection.isManagePermitted"
                        @click="duplicate(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="content_copy" />
                        </q-item-section>
                        <q-item-section>
                          Duplicate
                        </q-item-section>
                        <action-not-permitted-tooltip v-if="!collection.isManagePermitted" />
                      </q-item>
                      <q-separator v-if="((deletable || submodel || props.row.deletable === true) && props.row.deletable !== false) && collection.isDeletePermitted" />
                      <q-item
                        v-if="((deletable || submodel || props.row.deletable === true) && props.row.deletable !== false) && collection.isDeletePermitted"
                        v-close-popup
                        clickable
                        :disable="!collection.isDeletePermitted"
                        @click="deleteModel(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="delete" />
                        </q-item-section>
                        <q-item-section>
                          {{ props.row.isLocal || submodel ? 'Remove' : 'Delete' }}
                        </q-item-section>
                        <action-not-permitted-tooltip
                          v-if="!collection.isDeletePermitted"
                          action="delete"
                        />
                      </q-item>
                      <q-separator v-if="archivable && props.row.archivable !== false && !(submodelArchived || archived) && collection.isDeletePermitted" />
                      <q-item
                        v-if="archivable && props.row.archivable !== false && !(submodelArchived || archived) && collection.isDeletePermitted"
                        v-close-popup
                        clickable
                        :disable="!collection.isDeletePermitted"
                        @click="archiveModel(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="archive" />
                        </q-item-section>
                        <q-item-section>
                          Archive
                        </q-item-section>
                        <action-not-permitted-tooltip
                          v-if="!collection.isDeletePermitted"
                          action="delete"
                        />
                      </q-item>
                      <q-separator v-if="archivable && props.row.archivable !== false && (submodelArchived || archived) && collection.isDeletePermitted" />
                      <q-item
                        v-if="archivable && props.row.archivable !== false && (submodelArchived || archived) && collection.isDeletePermitted"
                        v-close-popup
                        clickable
                        :disable="!collection.isDeletePermitted"
                        @click="unarchiveModel(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="unarchive" />
                        </q-item-section>
                        <q-item-section>
                          Unarchive
                        </q-item-section>
                        <action-not-permitted-tooltip
                          v-if="!collection.isDeletePermitted"
                          action="delete"
                        />
                      </q-item>
                      <q-separator v-if="$admin && versioned" />
                      <q-item
                        v-if="$admin && versioned"
                        v-close-popup
                        clickable
                        @click="showHistory(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="history" />
                        </q-item-section>
                        <q-item-section>
                          View History
                        </q-item-section>
                      </q-item>
                      <template v-for="(action, i) in actions">
                        <div
                          v-if="!action.if || action.if(props.row)"
                          :key="`action=${i}`"
                        >
                          <q-separator />
                          <q-item
                            v-close-popup
                            clickable
                            :disabled="!action.disabled || action.disabled(props.row)"
                            @click="action.handler && (!action.disabled || !action.disabled(props.row)) && action.handler(props.row)"
                          >
                            <q-item-section avatar>
                              <q-icon :name="action.icon" />
                            </q-item-section>
                            <q-item-section>
                              {{ action.label }}
                            </q-item-section>
                            <q-tooltip v-if="action.disabled && action.disabled(props.row) && action.disabledTooltip">
                              {{ typeof action.disabledTooltip === 'function' ? action.disabledTooltip(props.row) : action.disabledTooltip }}
                            </q-tooltip>
                            <q-tooltip v-if="action.tooltip && !(action.disabled && action.disabled(props.row))">
                              {{ typeof action.tooltip === 'function' ? action.tooltip(props.row) : action.tooltip }}
                            </q-tooltip>
                          </q-item>
                        </div>
                      </template>
                    </q-list>
                  </q-menu>
                </q-btn>
              </div>
            </slot>
          </q-td>
        </template>

        <!-- Optional body slot override - if provided, it will override the default body-cell rendering -->
        <template
          v-if="$slots.body"
          #body="props"
        >
          <slot
            name="body"
            v-bind="props"
          />
        </template>

        <template #pagination="scope">
          <span v-if="collection.options.variables?.noCount">
            Page {{ scope.pagination.page }}
          </span>
          <span v-else>
            {{ (scope.pagination.rowsPerPage * scope.pagination.page) - scope.pagination.rowsPerPage + 1 }}-{{ scope.pagination.rowsPerPage * scope.pagination.page }}
            <span v-if="scope.pagination.rowsNumber">of {{ scope.pagination.rowsNumber }}</span>
          </span>
          <q-btn
            v-if="!(collection.options.variables?.noCount)"
            icon="first_page"
            color="responsive-grey-8"
            round
            dense
            flat
            :disable="scope.isFirstPage || loading || collection.loading"
            @click="scope.firstPage"
          />
          <q-btn
            icon="chevron_left"
            color="responsive-grey-8"
            round
            dense
            flat
            :disable="scope.isFirstPage || loading || collection.loading"
            @click="scope.prevPage"
          />

          <q-btn
            icon="chevron_right"
            color="responsive-grey-8"
            round
            dense
            flat
            :disable="scope.isLastPage || loading || collection.loading"
            @click="scope.nextPage"
          />
          <q-btn
            v-if="!(collection.options.variables?.noCount)"
            icon="last_page"
            color="responsive-grey-8"
            round
            dense
            flat
            :disable="scope.isLastPage || loading || collection.loading"
            @click="scope.lastPage"
          />
        </template>

        <template #item="props">
          <slot
            name="card"
            v-bind="props"
          />
        </template>
      </q-table>
    </component>

    <slot ref="main" />
    <set-display-order-dialog
      v-if="orderable && !archived"
      ref="orderDialog"
      :title="displayOrderTitle"
      :description="displayOrderDescription"
      :list="archiveFilteredOrdered"
      :order-options="orderOptions"
      :order-field="orderField"
      :loading="collection.loading"
      @reorder="handleReorder"
    />
    <add-from-library-dialog
      v-if="hasLibrary && libraryCollection"
      ref="libraryDialog"
      v-model="showAddFromLibraryDialog"
      :columns="libraryColumns"
      :collection="collection"
      :library-collection="libraryCollection"
    />
    <versions-dialog
      v-if="versioned"
      ref="versionsDialog"
    />
    <archive-confirmation-dialog
      v-if="archivable"
      ref="archiveConfirmationDialog"
      v-model="showArchiveConfirmationDialog"
      @before-archive="beforeArchive"
      @after-archive="afterArchive"
    />
  </div>
</template>

<script>
import { exportFile, Notify } from 'quasar'
import Model, { encoders, IsoDate, MODEL_LEVEL } from 'src/models/model'
import { RATING_ICONS, RATING_ICONS_COLORS } from 'src/models/visit-review/VisitReview'
import AddFromLibraryDialog from 'src/modules/admin/components/AddFromLibraryDialog'
import ArchiveConfirmationDialog from 'src/modules/admin/components/archiving/ArchiveConfirmationDialog'
import AuditMessagesIcon from 'src/modules/admin/components/AuditMessagesIcon'
import EnglishRequiredTooltip from 'src/modules/admin/components/EnglishRequiredTooltip'
import ChipCell from 'src/modules/admin/components/model-table/components/ChipCell'
import DateTimeCell from 'src/modules/admin/components/model-table/components/DateTimeCell'
import IconCell from 'src/modules/admin/components/model-table/components/IconCell'
import LibraryLinkCell from 'src/modules/admin/components/model-table/components/LibraryLinkCell'
import ModelChipCell from 'src/modules/admin/components/model-table/components/ModelChipCell'
import MoneyCell from 'src/modules/admin/components/model-table/components/MoneyCell'
import ModelLevelIcon from 'src/modules/admin/components/ModelLevelIcon'
import SetDisplayOrderDialog from 'src/modules/admin/components/SetDisplayOrderDialog'
import EffectiveTypeDetail from 'src/modules/admin/effective-type/EffectiveTypeDetail'
import QueryEncodableOptionsMixin from 'src/modules/admin/mixins/QueryEncodableOptionsMixin'
import VersionsDialog from 'src/modules/admin/versions/VersionsDialog'
import { defineAsyncComponent } from 'vue'

const defaultColumnProps = { align: 'left', headerStyle: 'vertical-align: bottom' }
const ROWS_PER_PAGE_OPTIONS = [5, 7, 10, 15, 20, 25, 50]

export default {
  name: 'ModelTable',
  components: {
    ArchiveConfirmationDialog,
    AuditMessagesIcon,
    ActionNotPermittedTooltip: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/components/ActionNotPermittedTooltip')),
    ChipCell,
    DateTimeCell,
    EnglishRequiredTooltip,
    IconCell,
    SetDisplayOrderDialog,
    AddFromLibraryDialog,
    LibraryLinkCell,
    VersionsDialog,
    EffectiveTypeDetail,
    ModelChipCell,
    ModelLevelIcon,
    MoneyCell,
    TranslateLanguageSelect: defineAsyncComponent(() => import('src/modules/admin/components/TranslateLanguageSelect'))
  },
  mixins: [QueryEncodableOptionsMixin],
  props: {
    collection: {
      type: Object,
      required: true
    },
    columns: {
      type: Array,
      required: false,
      default: null
    },
    actions: {
      type: Array,
      required: false,
      default () {
        return []
      }
    },
    submodel: {
      type: Boolean,
      required: false,
      default: false
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false
    },
    editModeAddUsingForm: {
      type: Boolean,
      required: false,
      default: false
    },
    constructorData: {
      type: Object,
      required: false,
      default: () => ({})
    },
    orderable: {
      type: Boolean,
      required: false,
      default: false
    },
    showFilter: {
      type: Boolean,
      required: false,
      default: undefined
    },
    showModelLevel: {
      type: Boolean,
      required: false,
      default: true
    },
    showAdd: {
      type: Boolean,
      required: false,
      default: true
    },
    addLabel: {
      type: String,
      required: false,
      default: null
    },
    showEdit: {
      type: Boolean,
      required: false,
      default: true
    },
    emitOnlyAdd: {
      type: Boolean,
      required: false,
      default: false
    },
    hasLibrary: {
      type: Boolean,
      required: false,
      default: false
    },
    libraryCollection: {
      type: Object,
      required: false,
      default: null
    },
    libraryColumns: {
      type: Array,
      required: false,
      default: null
    },
    linkFilter: {
      type: Function,
      required: false,
      default: null
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    displayOrderTitle: {
      type: String,
      required: false,
      default: 'Set Display Order'
    },
    displayOrderDescription: {
      type: String,
      required: false,
      default: null
    },
    editLabel: {
      type: String,
      required: false,
      default: 'Edit'
    },
    rowName: {
      type: Function,
      required: false,
      default: null
    },
    orderOptions: {
      type: Array,
      required: false,
      default: () => ([])
    },
    sortBy: {
      type: String,
      required: false,
      default: null
    },
    descending: {
      type: Boolean,
      required: false,
      default: true
    },
    initialLimit: {
      type: Number,
      required: false,
      default: 10
    },
    hidePaginate: {
      type: Boolean,
      required: false,
      default: false
    },
    cellClass: {
      type: Function,
      required: false,
      default: null
    },
    rowKey: {
      type: String,
      required: false,
      default: 'id'
    },
    editOnRowClick: {
      type: Boolean,
      required: false,
      default: true
    },
    selection: {
      type: String,
      required: false,
      default: 'none'
    },
    initialSelection: {
      type: Array,
      required: false,
      default () {
        return []
      }
    },
    dialog: {
      type: Boolean,
      required: false,
      default: false
    },
    filterByDataArray: {
      type: Array,
      required: false,
      default: null
    },
    orderField: {
      type: String,
      required: false,
      default: 'displayOrder'
    },
    headerButtons: {
      type: Array,
      required: false,
      default: () => ([])
    },
    grid: {
      type: Boolean,
      required: false,
      default: false
    },
    hideTop: {
      type: Boolean,
      required: false,
      default: false
    },
    bordered: {
      type: Boolean,
      required: false,
      default: true
    },
    denseTable: {
      type: Boolean,
      required: false,
      default: false
    },
    flat: {
      type: Boolean,
      required: false,
      default: true
    },
    square: {
      type: Boolean,
      required: false,
      default: false
    },
    showExport: {
      type: Boolean,
      required: false,
      default: false
    },
    showArchived: {
      type: Boolean,
      required: false,
      default: false
    },
    dense: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['filter', 'selected-input', 'change', 'row-clicked', 'table-sorted', 'icon-click', 'edit', 'reorder'],
  data () {
    return {
      showDialog: false,
      actionForms: {},
      requestedFilter: '',
      currentFilter: '',
      pagination: null,
      selected: this.initialSelection,
      showAddFromLibraryDialog: false,
      loading: false,
      exportOption: null,
      gridOn: this.grid,
      reactiveTriggerKey: 0,
      archived: false,
      submodelArchived: false,
      showArchiveConfirmationDialog: false
    }
  },
  configuration: ['currentSite', 'currentCircuit'],
  context: ['currentUser'],
  computed: {
    defaultQueryEncodableOptions () {
      return {
        sortBy: this.sortBy, // initial value from props
        descending: this.descending, // initial value from props
        page: this.paginate ? 1 : 0,
        rowsPerPage: this.paginate ? this.initialLimit : 0,
        archived: false,
        search: ''
      }
    },
    currentQueryEncodableOptions () {
      const { sortBy, descending, page, rowsPerPage } = this.pagination || {}
      let optionsToEncode = { sortBy, descending, page, rowsPerPage, search: this.currentFilter, archived: this.archived }
      optionsToEncode = _.pickBy(optionsToEncode, (value) => _.isNumber(value) || _.isBoolean(value) || !_.isEmpty(value))
      optionsToEncode = _.omitBy(optionsToEncode, (value, key) => value === this.defaultQueryEncodableOptions[key])
      return optionsToEncode
    },
    currentLanguage () {
      return this.collection.language
    },
    processedColumns () {
      const columns = _.map(this.columns, column => {
        if (column.hide) return null

        if (column.name === 'library' && !this.hasLibrary) return null

        column = _.extend({}, defaultColumnProps, column)

        const key = (column.name || column.field)

        if (!column.label && column.field === 'id') {
          column.label = 'ID'
        }
        if (!column.label) {
          column.label = _.startCase(key)
        }
        if (!column.name) {
          column.name = column.field
        }

        const field = this.collection.fields?.[column.name]

        if (field?.sortable) {
          column.sortable = true
        }

        return column
      })

      return _.compact(columns)
    },
    canSetOrder () {
      return this.orderable && !this.currentFilter && !this.pagination.sortBy && this.collection.isManagePermitted
    },
    shouldShowFilter () {
      return this.showFilter === undefined ? this.paginate : this.showFilter
    },
    shouldShowLanguageSelect () {
      return this.$admin &&
        this.currentCircuit.supportsTranslations &&
        this.collection.isTranslationEditingAllowed &&
        this.collection.isTranslatable
    },
    archiveFiltered () {
      if (!this.archivable || this.showArchived) {
        return this.collection.data
      }

      return _.filter(this.collection.data, model => {
        return (this.submodelArchived || this.archived) ? model.isArchived : model.isUnarchived
      })
    },
    archiveFilteredOrdered () {
      return this.orderField === 'displayOrder' ? this.archiveFiltered : _.sortBy(this.archiveFiltered, this.orderField)
    },
    archivedModels () {
      return this.archivable ? _.filter(this.collection.data, row => row.isArchived) : []
    },
    filteredData () {
      if (this.filterByDataArray) {
        return this.filterByDataArray
      } else if (this.paginate) {
        return this.archiveFiltered
      } else {
        return (this.currentFilter && this.$refs.table) ? this.$refs.table.filterMethod(this.archiveFiltered, this.currentFilter) : this.archiveFiltered
      }
    },
    paginate () {
      return this.collection.paginate && !this.collection.parent
    },
    auditSummary () {
      return this.auditByType?.data
    },
    deletable () {
      return this.collection.deletable
    },
    duplicatable () {
      return this.collection.duplicatable
    },
    versioned () {
      return this.collection.versioned
    },
    archivable () {
      return this.collection.archivable
    }
  },
  // For mixin
  replaceCurrentQueryDecodedOptionsWatcher: true,
  watch: {
    'collection.count' () {
      this.pagination.rowsNumber = this.computeRowsNumber()
    },
    'collection.data.length' () {
      this.pagination.rowsNumber = this.computeRowsNumber()
    },
    initialSelection () {
      this.selected = this.initialSelection
    },
    selected () {
      this.$emit('selected-input', this.selected)
    },
    requestedFilter () {
      if (!this.requestedFilter) {
        this.currentFilter = ''
      }
    },
    // This is not `immediate: true` because the `mounted()` takes care of the initial setup and request
    currentQueryDecodedOptions () {
      if (!this.shouldEncodeAndDecodeQueryParams) return
      if (this.$refs.libraryDialog?.shown) return

      if (!_.isEqual(this.currentQueryDecodedOptions, this.currentQueryEncodableOptions)) {
        this.initializeQueryEncodableOptions()
        this.onRequest({
          pagination: this.pagination,
          filter: this.currentFilter
        })
      }
    }
  },
  async mounted () {
    this.pagination = {
      sortBy: this.sortBy,
      descending: this.descending,
      page: this.paginate ? 1 : 0,
      rowsPerPage: this.paginate ? this.initialLimit : 0,
      rowsNumber: this.computeRowsNumber()
    }
    if (this.shouldEncodeAndDecodeQueryParams) {
      this.initializeQueryEncodableOptions()
    }

    // If the parent component sets `immediate: false` for model fetching, this ensures
    // we perform the initial fetch including any decoded query param options.
    if (this.paginate) {
      this.onRequest({
        pagination: this.pagination,
        filter: this.currentFilter
      })
    } else if (!this.isSubmodel()) {
      await this.collection.ensureLoaded()
    }
  },
  async created () {
    _.extend(this, { MODEL_LEVEL, RATING_ICONS, RATING_ICONS_COLORS, ROWS_PER_PAGE_OPTIONS, configuration: Model.configuration, IsoDate, encoders })
  },
  models: {
    auditByType: {
      variables () {
        return {
          skip: !this.collection.hasAuditSummary,
          modelType: this.collection.typeName
        }
      },
      immediate: true
    }
  },
  methods: {
    computeRowsNumber () {
      const collectionCurrentLength = this.collection?.data?.length || this.collection?.length || 0
      if (!this.paginate) {
        // Pagination is not supported for the current model, so we can return the collection current length.
        return collectionCurrentLength
      } else if (_.isNil(this.pagination) || _.isEmpty(this.pagination)) {
        // Pagination is supported, but not yet initialized. The pagination data is null
        // at the start so we fallback to the collection current length.
        return collectionCurrentLength
      }

      // At this point, pagination is supported and initialized. The API will return either
      // an accurate count, or an isMore boolean, but not both.
      if (_.isNil(this.collection.isMore) && _.isFinite(this.collection.count)) {
        // We have an accurate total row count, so we can return it.
        return this.collection.count
      }

      // At this point we are not getting a total row count from the API, so we need to calculate it.
      const rowsPerPage = this.pagination.rowsPerPage || this.initialLimit
      const page = this.pagination.page || 1
      if (this.collection.isMore) {
        // We don't know how many more rows are available, just that there are more rows than
        // have been shown so far, which is at least one more row than fits in the current pages.
        return rowsPerPage * page + 1
      } else {
        // We know there are no more rows available, so we know the total number of rows
        // is every prior pages' rows count + the current page's row count.
        return rowsPerPage * (page - 1) + collectionCurrentLength
      }
    },
    initializeQueryEncodableOptions () {
      this.pagination = {
        ..._.omit(this.defaultQueryEncodableOptions, ['search', 'archived']),
        ..._.omit(this.currentQueryDecodedOptions, ['search', 'archived']),
        rowsNumber: this.computeRowsNumber()
      }
      this.requestedFilter = this.currentQueryDecodedOptions.search || this.defaultQueryEncodableOptions.search
      this.currentFilter = this.requestedFilter
      this.archived = this.currentQueryDecodedOptions.archived || this.defaultQueryEncodableOptions.archived
    },
    decodeUrlParamOption (key, value) {
      switch (key) {
        case 'sortBy':
          return value
        case 'descending':
          return value === 'true'
        case 'page':
          return this.paginate && parseInt(value) >= 1 ? parseInt(value) : undefined
        case 'rowsPerPage':
          if (this.paginate && _.includes(ROWS_PER_PAGE_OPTIONS, parseInt(value))) {
            return parseInt(value)
          } else {
            return undefined
          }
        case 'archived':
          return this.archivable ? value === 'true' : undefined
        case 'search':
          return value
        default:
          return undefined
      }
    },
    isSubmodel () {
      // This needs to be a method as this.collection.isSubmodel is not reactive
      return (this.submodel || this.collection.isSubmodel)
    },
    show () {
      this.showDialog = true
    },
    close () {
      this.showDialog = false
    },
    add () {
      if (this.editMode && !this.editModeAddUsingForm) {
        this.collection.add(this.constructorData, true)
      } else {
        this.triggerAction('add', null, this.constructorData)
      }
    },
    edit (model) {
      this.triggerAction('edit', model)
    },
    download (url) {
      window.open(url)
    },
    openLinkInNewTab (url) {
      window.open(url, '_blank')
    },
    async duplicate (originalModel) {
      this.loading = true
      const model = new this.collection.constructor()

      // Handle Report models that need the atlas parameter
      let duplicateResult
      if (model.atlasable && originalModel.atlas !== undefined) {
        duplicateResult = await model.duplicate([originalModel], originalModel.atlas)
      } else {
        duplicateResult = await model.duplicate([originalModel])
      }

      const { errors } = duplicateResult

      // Refetch collection
      if (this.collection.options.baseQuery) {
        await this.collection.fetch(true)
      } else if (this.collection.parent?.options?.baseQuery && this.collection.parent.typeName !== 'ClientConfig') {
        await this.collection.parent.fetch(true)
      }
      this.$emit('change')

      this.loading = false

      if (errors && errors.length > 0) {
        Notify.create({
          message: 'Error duplicating items',
          type: 'negative',
          position: 'top'
        })
      }
    },
    handleRowClick (event, row) {
      if (_.some(_.values(event.path), el => {
        return _.includes(el.classList, 'no-select')
      })) return

      if (this.selection === 'none') {
        this.editOnRowClick && this.edit(row)
      } else {
        if (_.some(this.selected, { id: row.id })) {
          this.selected = _.differenceBy(this.selected, [row], 'id')
        } else {
          this.selected = _.unionBy(this.selected, [row], 'id')
        }
      }
      this.$emit('row-clicked', row)
    },
    triggerAction (action, model, initialData = {}) {
      this.$emit(action, model)
      if (!(this.emitOnlyAdd && action === 'add') && !!this.actionForms[action]?.show) {
        this.actionForms[action].show(model, initialData)
      }
    },
    setActionForm (action, form) {
      this.actionForms[action] = form
    },
    removeActionForm (action, form) {
      if (this.actionForms[action] === form) {
        delete this.actionForms[action]
      }
    },
    async changeTranslationLanguage (language) {
      if (this.submodel) return
      this.collection.resetToLanguage(language)
      await this.reloadData()
    },
    async reloadData () {
      if (this.archivable) {
        this.collection.options.variables = {
          ...this.collection.options.variables,
          archived: this.archived
        }
      }

      if (this.collection.hasAncestor(this.collection.library)) {
        await this.collection.library.fetch(true)
      } else if (this.paginate) {
        this.pagination.page = 1
        this.collection.options.variables.offset = 0
        this.onRequest({
          pagination: this.pagination,
          filter: this.currentFilter
        })
      } else {
        await this.collection.fetch(true)
      }
    },
    getEditorPlaceholder (column, model) {
      if (column.editor.placeholder) {
        return typeof column.editor.placeholder === 'function' ? column.editor.placeholder(model) : column.editor.placeholder
      }
      return ''
    },
    async toggleReceiveLibraryUpdates (model) {
      model.receiveLibraryUpdates = !model.receiveLibraryUpdates
      await model.save()
    },
    async archiveModel (model) {
      await this.$refs.archiveConfirmationDialog.show(
        model.typeName,
        model.id
      )
    },
    beforeArchive (_modelId) {
      this.collection.loading = true
    },
    afterArchive (_modelId) {
      this.collection.loading = false
      if (this.collection.isSubmodel) {
        this.collection.parent.fetch(true)
      } else {
        this.collection.fetch(true)
      }
    },
    unarchiveModel: async function (model) {
      this.$q.dialog({
        title: 'Confirm Unarchive',
        message: `Are you sure you want to unarchive ${this.rowName ? this.rowName(model) : 'this row'}?`,
        ok: {
          label: 'Unarchive',
          color: 'primary',
          autofocus: true
        },
        cancel: {
          label: 'Cancel',
          color: 'secondary'
        },
        focus: 'cancel'
      }).onOk(async () => {
        this.collection.loading = true
        const results = await model.unarchive()
        if (!_.isEmpty(results?.errors)) {
          this.$mh.showErrors(results.errors)
        }
        this.collection.loading = false

        if (this.collection.isSubmodel) {
          this.collection.parent.fetch(true)
        } else {
          this.collection.fetch(true)
        }
      })
    },
    showActions (row) {
      return this.showEdit || this.duplicatable || ((this.deletable || this.submodel || row.deletable === true) && row.deletable !== false) || this.archivable || (this.$admin & this.versioned)
    },
    async deleteModel (model) {
      // If this is just a local version, we haven't saved it so just delete
      if (model.isLocal || this.submodel) {
        this.collection.remove(model)
        return
      }

      // This gets complicated if there are unsaved changes. Don't let them proceed if the parent is dirty.
      if (this.collection.parent?.isDirty) {
        this.$q.dialog({
          title: 'Unsaved changes',
          message: 'Please save changes before deleting',
          ok: {
            label: 'Ok',
            color: 'primary',
            autofocus: true
          },
          focus: 'ok'
        })
        return
      }

      this.$q.dialog({
        title: 'Confirm Delete',
        message: `${model?.deletionWarningText ? 'Warning: ' + model.deletionWarningText + '. ' : ''} Are you sure you want to delete ${this.rowName ? this.rowName(model) : 'this row'}?`,
        ok: {
          label: 'Delete',
          color: model?.deletionWarningText ? 'negative' : 'primary',
          autofocus: true
        },
        cancel: {
          label: 'Cancel',
          color: 'secondary'
        },
        focus: 'cancel'
      }).onOk(async () => {
        this.collection.loading = true
        const { errors } = await model.delete()
        if (errors.length === 0) {
          this.collection.remove(model)
          if (this.collection.parent) {
            this.collection.parent.handleSubmodelSave()
          }
          this.$emit('change')
        } else {
          this.$mh.showErrors(errors)
        }
        this.collection.loading = false
      })
    },
    showHistory (model) {
      this.$refs.versionsDialog.show(model.typeName, model.id)
    },
    async setOrder () {
      // Need to load all pages before we can properly set the order
      this.pagination.rowsPerPage = 0
      await this.onRequest({ pagination: this.pagination })
      this.$refs.orderDialog.show()
    },
    async handleReorder ({ reorderedList, orderField = 'displayOrder' }) {
      this.collection.data = reorderedList.concat(this.archivedModels)
      this.collection.validateDisplayOrder(orderField)

      this.$emit('reorder', { reorderedList, orderField })

      if (!this.submodel) {
        this.collection.loading = true
        // This prevents the subscription trigger from showing partial results and strange sorting
        this.collection.silent = true

        const changedModel = _(this.collection.data).filter({ isDirty: true }).value()
        await Model.configuration.bulkSetOrder(changedModel, orderField)

        this.collection.loading = false
        this.collection.silent = false

        await Model.configuration.validate()
      }
    },
    applySubmodelFilter (column, submodel) {
      if (column.filterDisabled) return

      if (!submodel) {
        delete this.collection.options.variables[column.name + 'Id']
      } else {
        this.collection.options.variables[column.name + 'Id'] = submodel.id
      }

      if (this.paginate) {
        this.pagination.page = 1
        this.collection.options.variables.offset = 0
        this.onRequest({
          pagination: this.pagination,
          filter: this.currentFilter
        })
      } else {
        this.collection.fetch(true)
      }
    },
    getSortFieldName (sortFieldName) {
      const field = this.collection.fields?.[sortFieldName]
      // If field has encoders.cents encoder, transform to backend field name for sorting
      return field?.encode === this.encoders?.cents ? `${sortFieldName}Cents` : sortFieldName
    },
    async onRequest (props) {
      // We want to avoid the duplicate request fired when this.currentFilter is changed on mounted() when reading the value
      // from URL query params because QTable will always try to set the page number back to 1 when the filter string changes.
      // We want to avoid the page number reset on the first time we read any filter URL query param value.
      if (this.collection.loading) return

      this.pagination.sortBy = props.pagination.sortBy
      this.pagination.descending = props.pagination.descending
      this.pagination.page = props.pagination.page
      this.pagination.rowsPerPage = props.pagination.rowsPerPage

      if (this.archivable) {
        this.collection.options.variables = {
          ...this.collection.options.variables,
          archived: this.archived
        }
      }

      if (this.paginate) {
        this.collection.options.variables = {
          ...this.collection.options.variables,
          searchString: props.filter,
          orderBy: this.getSortFieldName(props.pagination.sortBy),
          descending: props.pagination.descending,
          limit: props.pagination.rowsPerPage ? props.pagination.rowsPerPage : 200,
          offset: (props.pagination.page - 1) * (props.pagination.rowsPerPage)
        }
        await this.collection.fetch(true)
        this.pagination.rowsNumber = this.computeRowsNumber()
      } else {
        this.currentFilter = props.filter
        const tableData = this.$refs.table.sortMethod?.(this.$refs.table.data, props.pagination.sortBy, props.pagination.descending)
        this.$emit('table-sorted', tableData)
      }
    },
    async handleChange (currentLanguage = null, validateConfigurationBeforeFetchingCollection = false) {
      if (this.submodel) return
      // Refetch collection
      let collectionToFetch
      if (this.collection.options.baseQuery) {
        collectionToFetch = this.collection
      } else if (this.collection.parent?.options?.baseQuery && this.collection.parent.typeName !== 'ClientConfig') {
        collectionToFetch = this.collection.parent
      }
      if (collectionToFetch) {
        if (currentLanguage && currentLanguage !== collectionToFetch.language) {
          collectionToFetch.resetToLanguage(currentLanguage)
        }
        if (validateConfigurationBeforeFetchingCollection) {
          await Model.configuration.validate()
        }
        collectionToFetch.fetch(true)
      }
      this.$emit('change')
    },
    computeAuditLevel (audits) {
      const filteredAudits = _.filter(audits, audit => {
        if (audit.suppressed) {
          return false
        }

        if (!this.currentUser?.superUser && audit.indyOnly) {
          return false
        }
        return true
      })

      if (_.some(filteredAudits, { level: 'error' })) {
        return 'error'
      }

      if (_.some(filteredAudits, { level: 'warning' })) {
        return 'warning'
      }
      return 'info'
    },
    exportTable () {
      let columns = this.$refs.table.columns
      columns = _.filter(columns, column => column?.name !== 'actions')
      const rows = this.$refs.table.data || this.$refs.table.rows
      // naive encoding to csv format
      const content = [columns.map(col => this.wrapCsvValue(col.label))].concat(
        rows.map(row => columns.map(col => this.wrapCsvValue(
          this.getDataValue(col, row),
          col.format,
          row
        )).join(','))
      ).join('\r\n')

      const status = exportFile(this.collection.humanCollectionName + '-export-' + new Date().toISOString() + '.csv', content, 'text/csv')

      if (status !== true) {
        this.$q.notify({
          message: 'Browser denied file download...',
          color: 'negative',
          icon: 'warning'
        })
      }
    },
    getDataValue (column, row) {
      if (typeof column.field === 'function' && column?.component === 'chip') {
        return column.field(row)?.label
      } else if (column.component === 'model') {
        return row[column.field]?.displayName || ''
      } else if (column.component === 'icon') {
        if (column.name) {
          return row[column.name]
        }
      } else if (typeof column.component === 'object' && column.component?.methods?.exportString) {
        return column.component.methods.exportString(row)
      } else if (column.component === 'money') {
        const moneyValue = row[column.field]
        if (moneyValue && _.isNumber(moneyValue)) {
          return this.$i18n.n(moneyValue, 'currency')
        } else {
          return '-'
        }
      } else if (column.component === 'money-count') {
        const moneyValue = row[column.field]
        if (moneyValue && _.isNumber(moneyValue?.amount)) {
          return this.$i18n.n(moneyValue.amount, 'currency')
        } else {
          return '-'
        }
      } else if (column.component === 'money-hundredths') {
        const moneyHundrethValue = row[column.field]
        if (moneyHundrethValue && _.isNumber(moneyHundrethValue)) {
          return this.$i18n.n(moneyHundrethValue, 'currencyHundredths')
        } else {
          return '-'
        }
      } else if (typeof column.field === 'function') {
        return column.field(row)
      } else {
        return row[column.field === undefined ? column.name : column.field]
      }
    },
    wrapCsvValue (val, formatFn, row) {
      let formatted = formatFn !== undefined
        ? formatFn(val, row)
        : val

      formatted = formatted === undefined || formatted === null
        ? ''
        : String(formatted)

      formatted = formatted.split('"').join('""')
      /**
   * Excel accepts \n and \r in strings, but some other CSV parsers do not
   * Uncomment the next two lines to escape new lines
   */
      // .split('\n').join('\\n')
      // .split('\r').join('\\r')

      return `"${formatted}"`
    },
    toggleArchived () {
      if (this.collection.isSubmodel) {
        this.submodelArchived = !this.submodelArchived
      } else {
        this.archived = !this.archived
        this.reloadData()
      }
    }
  }
}
</script>
